import { Dispatch, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TRequestStatus } from "@models/msc.model";
import rolesService from "@/services/roles.service";
import { RootState } from "..";
import { TPermissionsCatalog, TRole } from "@/models/roles.model";
import { usersActions } from "./users.reducer";

type RolesState = {
  initialized: boolean;
  status: TRequestStatus;
  roles: TRole[];
  permissionsCatalog: TPermissionsCatalog | null;
  catalogInitialized: boolean;
};

//-----THUNK FUNCTIONS-----

const initialState: RolesState = {
  initialized: false,
  status: "IDLE",
  roles: [],
  permissionsCatalog: null,
  catalogInitialized: false,
};

const rolesSlice = createSlice({
  name: "roles",
  initialState,
  reducers: {
    startLoading(state) {
      state.status = "PENDING";
    },
    rollback(state, actions) {
      state.status = "IDLE";
      state.roles = actions.payload;
    },
    gotRoles(state, action) {
      state.roles = action.payload;
      state.status = "IDLE";
      state.initialized = true;
    },
    roleSaved(state, action) {
      state.roles.unshift(action.payload);
    },
    roleUpdated(state, action) {
      state.roles = state.roles.map((t) => (t.id === action.payload.id ? action.payload : t));
    },
    roleRemoved(state, action) {
      state.roles = state.roles.filter((t) => t.id !== action.payload);
    },
    gotPermissionsCatalog(state, action: PayloadAction<TPermissionsCatalog>) {
      state.permissionsCatalog = action.payload;
      state.catalogInitialized = true;
    },
  },
});

// ----------------------------

export const getAllRoles = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const {
    roles: { roles, initialized },
  } = getState();

  if (initialized) return roles;

  dispatch(rolesSlice.actions.startLoading());
  const response = await rolesService.getAll();
  dispatch(rolesSlice.actions.gotRoles(response));
  return response;
};

// ----------------------------
export const saveRole = (data: TRole) => async (dispatch: Dispatch) => {
  const response = await rolesService.save(data);
  dispatch(rolesSlice.actions.roleSaved(response));
  return response;
};

// ----------------------------
export const updateRole = (data: TRole) => async (dispatch: Dispatch) => {
  await rolesService.update(data);
  dispatch(rolesSlice.actions.roleUpdated(data));
};

// ----------------------------

export const removeRole = (roleId: number) => async (dispatch: Dispatch, getState: () => RootState) => {
  const { roles } = getState().roles;
  const rolesForRollBack = structuredClone(roles);
  dispatch(rolesSlice.actions.roleRemoved(roleId));

  try {
    await rolesService.removeById(roleId);
  } catch (error) {
    dispatch(rolesSlice.actions.rollback(rolesForRollBack));
    throw error;
  }
};

// ----------------------------

export const getPermissionsCatalog = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const { catalogInitialized, permissionsCatalog } = getState().roles;
  if (catalogInitialized && permissionsCatalog) return permissionsCatalog;

  const response = await rolesService.getPermissionsCatalog();
  dispatch(rolesSlice.actions.gotPermissionsCatalog(response));
  return response;
};

// ----------------------------

/**
 * Replace the full set of users assigned to a role via `PUT /roles/{roleId}/users`.
 * After the request succeeds, reflect the change in the users store:
 *   - Users in `selectedUserIds` get the role added to their `roles` array (if missing).
 *   - Users previously in the role but not in `selectedUserIds` get the role removed.
 */
export const syncRoleUsers =
  (roleId: number, selectedUserIds: number[]) => async (dispatch: Dispatch, getState: () => RootState) => {
    await rolesService.assignUsers(roleId, selectedUserIds);

    const { users } = getState().users;
    const selectedSet = new Set(selectedUserIds);

    users.forEach((user) => {
      const hadRole = user.roles?.includes(roleId) ?? false;
      const shouldHaveRole = selectedSet.has(user.id);
      if (hadRole === shouldHaveRole) return;

      const currentRoles = user.roles || [];
      const nextRoles = shouldHaveRole
        ? Array.from(new Set([...currentRoles, roleId]))
        : currentRoles.filter((id) => id !== roleId);

      dispatch(usersActions.userRolesUpdated({ id: user.id, roles: nextRoles }));
    });
  };

export default rolesSlice.reducer;
